import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';

import { useDisclosure } from '@chakra-ui/hooks';

import { updateEmployee } from 'api/employees';
import { useMutation, useQueryClient } from 'react-query';

import { successToast } from 'utils/successToast';

import { washHub } from 'store/slices';

import { Employee, UserStatusEnum } from 'types/user';
import { RefType, EditEmployeeModalProps } from './EditEmployeeModal.props';

import EditEmployeeModalView from './EditEmployeeModal.view';

const Container = forwardRef<RefType, EditEmployeeModalProps>((_, ref) => {
  const queryClient = useQueryClient();

  const currentWashHub = washHub.selectors.useSelectedWashHub();

  const [employee, setEmployee] = useState<Employee | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (e) => {
        setEmployee(e);

        onOpen();
      },
    }),
    [],
  );

  const { mutate, isLoading } = useMutation(updateEmployee, {
    onSuccess: () => {
      successToast('Employee Updated Successfully');

      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey?.[0] === `fetch-employees-${currentWashHub?._id}`,
      });

      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey?.[0] === 'fetch-super-admins',
      });

      setEmployee(null);

      onClose();
    },
  });

  const _props = {
    isOpen,
    onClose,
    isLoading,
    employee,
    onUpdate: async (values: any) => {
      const { washHub, isActive, ...restValues } = values;

      const payload: any = {
        ...restValues,
        _id: employee?._id,
        phone:
          values?.phone && values.phone !== '0'
            ? String(values.phone)
            : undefined,
      };

      // Set status based on the toggle
      if (isActive) {
        // Toggle is on (active) -> set to active
        payload.status = UserStatusEnum.ACTIVE;
      } else {
        // Toggle is off -> set to suspended
        payload.status = UserStatusEnum.SUSPENDED;
      }

      mutate(payload);
    },
  };

  return <EditEmployeeModalView {..._props} />;
});

const EditEmployeeModal = memo(Container);

export default EditEmployeeModal;
